import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, LayoutDashboard, LogOut, Rocket, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/pitches", label: "Pitches" },
  { to: "/live", label: "Live" },
  { to: "/events", label: "Events" },
  { to: "/rankings", label: "Rankings" },
  { to: "/pricing", label: "Pricing" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    toast.success("Logged out. See you soon!");
    navigate("/");
  };

  return (
    <header className="fixed top-0 w-full z-50 bg-[#050505] border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="EBN — Elite Business Network" className="h-10 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#F5B301]" : "text-zinc-400 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <button data-testid="nav-search-btn" aria-label="Search" className="p-2 rounded-full border border-white/10 text-zinc-400 hover:text-[#F5B301] hover:border-[#F5B301]/40 transition-colors">
            <Search size={16} />
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu-btn" className="flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full border border-white/15 hover:border-[#F5B301]/50 transition-colors">
                  <span className="w-7 h-7 rounded-full bg-[#F5B301] text-black text-xs font-bold flex items-center justify-center">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                  <span className="text-sm font-medium text-white max-w-[110px] truncate">{user.name}</span>
                  <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#F5B301]">
                    {user.role === "founder" ? <Rocket size={10} /> : <TrendingUp size={10} />} {user.role}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121212] border border-white/10 text-white min-w-[200px]">
                <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  <LayoutDashboard size={14} className="mr-2 text-[#F5B301]" /> {user.role === "founder" ? "My Pitches" : "My Watchlist"}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="nav-logout-btn" onClick={doLogout} className="cursor-pointer focus:bg-white/10 focus:text-white">
                  <LogOut size={14} className="mr-2 text-zinc-400" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <button data-testid="nav-login-btn" onClick={() => navigate("/login")} className="px-5 py-2 text-sm font-medium rounded-full border border-white/15 text-white hover:border-[#F5B301]/60 hover:text-[#F5B301] transition-colors">
                Login
              </button>
              <button data-testid="nav-signup-btn" onClick={() => navigate("/signup")} className="px-5 py-2 text-sm font-semibold rounded-full bg-[#F5B301] text-black hover:bg-[#D99E01] transition-colors">
                Sign Up
              </button>
            </>
          )}
        </div>
        <button data-testid="nav-mobile-toggle" className="lg:hidden p-2 text-zinc-300" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-[#0a0a0a] border-t border-white/5 px-6 py-4 space-y-3" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm text-zinc-300 hover:text-[#F5B301]">
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              <NavLink to="/dashboard" onClick={() => setOpen(false)} className="block text-sm text-[#F5B301] font-semibold">
                {user.role === "founder" ? "My Pitches" : "My Watchlist"}
              </NavLink>
              <button onClick={() => { setOpen(false); doLogout(); }} className="w-full mt-2 px-5 py-2 text-sm font-semibold rounded-full border border-white/15 text-white">Logout</button>
            </>
          ) : (
            <button onClick={() => { setOpen(false); navigate("/signup"); }} className="w-full mt-2 px-5 py-2 text-sm font-semibold rounded-full bg-[#F5B301] text-black">Sign Up</button>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
